import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Cpu, Palette, Megaphone, ArrowRight, Check, ChevronRight,
  Shield, Zap, Target, TrendingUp, Smartphone, ShoppingCart, Heart, Gamepad2,
  Send, Sparkles, Brain, Bot, BarChart3, Rocket, Globe, Play,
  CreditCard, Activity, ShoppingBag, Headphones
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { createContact } from '@/lib/api';
import { toast } from 'sonner';
import { Logo, LogoDark } from '@/components/Logo';
import { Seo } from '@/components/Seo';
import { FaqSection } from '@/components/FaqSection';

const LANDING_FAQS = [
  {
    q: 'Was ist marKIpilot?',
    a: 'marKIpilot ist eine KI-gestützte App-Marketing-Plattform der MYSYMP AG. Sie kombiniert Pay-per-Install-Kampagnen mit 16 spezialisierten KI-Marketing-Agenten (10 Standard-Agenten kostenlos, 6 Premium-Agenten) für Strategie, Content, Performance, CRM und Analytics.'
  },
  {
    q: 'Was kostet marKIpilot?',
    a: 'Der Free-Plan mit 10 Standard-KI-Agenten ist kostenlos. Der Starter-Plan kostet CHF 299 pro Monat (2 Premium-Agenten, 100 Anfragen/Monat), der Professional-Plan CHF 699 pro Monat (alle 6 Premium-Agenten, unbegrenzte Anfragen). Enterprise-Konditionen auf Anfrage.'
  },
  {
    q: 'Was bedeutet Pay-per-Install (PPI)?',
    a: 'Pay-per-Install ist ein Abrechnungsmodell im App-Marketing: Sie zahlen einen Fixpreis nur für verifizierte App-Downloads – nicht für Klicks, Impressionen oder Streuverlust. Das macht Akquisitionskosten planbar und transparent.'
  },
  {
    q: 'Brauche ich eine Kreditkarte, um zu starten?',
    a: 'Nein. Die Registrierung und der Free-Plan sind ohne Kreditkarte nutzbar. Der Login funktioniert klassisch mit Passwort oder passwortlos per Magic Link an Ihre E-Mail-Adresse.'
  },
  {
    q: 'Welche KI-Modelle nutzt marKIpilot?',
    a: 'Ein Smart Router wählt automatisch das passende Modell für Ihre Anfrage – verfügbar sind Modelle von OpenAI (GPT), Anthropic (Claude) und Google (Gemini). Die manuelle Modellwahl ist ebenfalls möglich.'
  },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] } }
};

const fadeInScale = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: [0.2, 0.8, 0.2, 1] } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

// Animated gradient orb component
const GradientOrb = ({ className, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 1.5, delay, repeat: Infinity, repeatType: "reverse" }}
    className={`absolute rounded-full blur-3xl ${className}`}
  />
);

export default function LandingPage() {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', app_name: '', budget: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.first_name) {
      toast.error('Bitte füllen Sie mindestens Vorname und E-Mail aus.');
      return;
    }
    setSending(true);
    try {
      await createContact(form);
      toast.success('Vielen Dank! Wir melden uns in Kürze bei Ihnen.');
      setForm({ first_name: '', last_name: '', email: '', app_name: '', budget: '', message: '' });
    } catch (err) {
      toast.error('Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.');
    } finally {
      setSending(false);
    }
  };

  const industries = [
    { 
      icon: CreditCard, 
      title: 'Fintech & Banking', 
      desc: 'Vertrauen aufbauen, KYC optimieren, Conversions maximieren.',
      image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=400&h=250&fit=crop',
      stats: '↑ 340% Conversion',
      color: 'from-blue-600 to-cyan-500',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/20'
    },
    { 
      icon: Gamepad2, 
      title: 'Gaming & Entertainment', 
      desc: 'Massen-UA, Viral Loops, In-App Purchase Optimierung.',
      image: 'https://images.unsplash.com/photo-1605957073056-34ea7e8dafea?w=400&h=250&fit=crop',
      stats: '2.5M+ Installs',
      color: 'from-purple-600 to-pink-500',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/20'
    },
    { 
      icon: Activity, 
      title: 'Health & Fitness', 
      desc: 'Subscription-Funnels, Paywall-Tests, Retention-Strategien.',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=250&fit=crop',
      stats: '65% Trial→Paid',
      color: 'from-emerald-600 to-teal-500',
      bgColor: 'bg-emerald-500/10',
      borderColor: 'border-emerald-500/20'
    },
    { 
      icon: ShoppingBag, 
      title: 'E-Commerce & Retail', 
      desc: 'ROAS-Optimierung, Re-Engagement, Cart Recovery.',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=250&fit=crop',
      stats: '8.2x ROAS',
      color: 'from-orange-600 to-amber-500',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/20'
    },
    { 
      icon: Headphones, 
      title: 'Streaming & Media', 
      desc: 'Content-Discovery, Binge-Retention, Premium-Upgrades.',
      image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=400&h=250&fit=crop',
      stats: '↑ 180% LTV',
      color: 'from-red-600 to-rose-500',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/20'
    },
    { 
      icon: Globe, 
      title: 'Travel & Mobility', 
      desc: 'Location-Based Ads, Booking-Funnels, Loyalty-Programme.',
      image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&h=250&fit=crop',
      stats: '€2.10 CPI',
      color: 'from-sky-600 to-indigo-500',
      bgColor: 'bg-sky-500/10',
      borderColor: 'border-sky-500/20'
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-hidden">
      <Seo
        title="KI-gestütztes App-Marketing mit Pay-per-Install"
        description="Dein KI-Pilot für App-Wachstum: Pay-per-Install – du zahlst nur für verifizierte Downloads. 16 KI-Marketing-Agenten für Strategie, Content & Performance."
        path="/"
      />
      {/* Navigation - with iPhone Safe Area */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-white/5" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shadow-lg shadow-teal-500/25">
                <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
              </div>
              <span className="font-display font-bold text-base sm:text-lg">
                mar<span className="text-teal-400">KI</span>pilot
              </span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <a href="#ki" className="text-sm text-slate-400 hover:text-white transition-colors">KI-Power</a>
              <a href="#industries" className="text-sm text-slate-400 hover:text-white transition-colors">Branchen</a>
              <a href="#services" className="text-sm text-slate-400 hover:text-white transition-colors">Leistungen</a>
              <a href="#process" className="text-sm text-slate-400 hover:text-white transition-colors">Prozess</a>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Link to="/login">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white text-xs sm:text-sm px-2 sm:px-3">Anmelden</Button>
              </Link>
              <a href="#contact">
                <Button size="sm" className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white border-0 shadow-lg shadow-teal-500/25 text-xs sm:text-sm px-3 sm:px-4">
                  <span className="hidden sm:inline">Kostenloses </span>Audit
                </Button>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - with safe area padding */}
      <section className="relative min-h-screen flex items-center" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 4rem)' }}>
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <GradientOrb className="w-[600px] h-[600px] -top-40 -left-40 bg-teal-500/20" delay={0} />
          <GradientOrb className="w-[500px] h-[500px] top-1/2 right-0 bg-cyan-500/15" delay={0.5} />
          <GradientOrb className="w-[400px] h-[400px] bottom-0 left-1/3 bg-purple-500/10" delay={1} />
          {/* Grid Pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm text-sm mb-8">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
                </span>
                <span className="text-slate-300">KI-gestütztes App Marketing</span>
              </motion.div>

              <motion.h1 variants={fadeInUp} className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] mb-6">
                <span className="text-white">Ihr </span>
                <span className="bg-gradient-to-r from-teal-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent">KI-Pilot</span>
                <br />
                <span className="text-white">für App-Wachstum</span>
              </motion.h1>

              <motion.p variants={fadeInUp} className="text-lg sm:text-xl text-slate-400 mb-8 max-w-lg leading-relaxed">
                mar<span className="text-teal-400 font-semibold">KI</span>pilot verbindet künstliche Intelligenz mit App-Marketing-Expertise. 
                <span className="text-white font-medium"> Pay-per-Install</span> – Sie zahlen nur für verifizierte Downloads.
              </motion.p>

              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 mb-12">
                <a href="#contact">
                  <Button size="lg" className="group bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white border-0 h-14 px-8 text-base shadow-xl shadow-teal-500/25">
                    Kostenloses KI-Audit
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
                <a href="#ki">
                  <Button variant="outline" size="lg" className="h-14 px-8 text-base border-white/10 bg-white/5 hover:bg-white/10 text-white backdrop-blur-sm">
                    <Play className="w-4 h-4 mr-2" />
                    So funktioniert's
                  </Button>
                </a>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex items-center gap-8">
                <div className="text-center">
                  <div className="font-display text-3xl font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">500K+</div>
                  <p className="text-xs text-slate-500 mt-1">Installs geliefert</p>
                </div>
                <div className="w-px h-12 bg-white/10" />
                <div className="text-center">
                  <div className="font-display text-3xl font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">40+</div>
                  <p className="text-xs text-slate-500 mt-1">App-Partner</p>
                </div>
                <div className="w-px h-12 bg-white/10" />
                <div className="text-center">
                  <div className="font-display text-3xl font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">24/7</div>
                  <p className="text-xs text-slate-500 mt-1">KI-Optimierung</p>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image / Visual */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                {/* Main Dashboard Image */}
                <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-teal-500/10">
                  <img 
                    src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop" 
                    alt="KI Analytics Dashboard" 
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />
                  
                  {/* Floating Stats Card */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                    className="absolute bottom-4 left-4 right-4 bg-slate-900/90 backdrop-blur-xl rounded-xl p-4 border border-white/10"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-400">Heutige Installs</p>
                        <p className="text-2xl font-bold text-white">12,847</p>
                      </div>
                      <div className="flex items-center gap-1 text-teal-400 text-sm">
                        <TrendingUp className="w-4 h-4" />
                        +23.5%
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Floating KI Badge */}
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1 }}
                  className="absolute -top-4 -right-4 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl p-4 shadow-xl shadow-teal-500/30"
                >
                  <Brain className="w-8 h-8 text-white" />
                </motion.div>

                {/* Floating Performance Card */}
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.2 }}
                  className="absolute top-1/4 -left-8 bg-slate-900/90 backdrop-blur-xl rounded-xl p-3 border border-white/10 shadow-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">CPI</p>
                      <p className="text-lg font-bold text-white">€2.34</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div 
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center p-2"
          >
            <div className="w-1.5 h-1.5 rounded-full bg-teal-400" />
          </motion.div>
        </motion.div>
      </section>

      {/* KI Power Section */}
      <section id="ki" className="py-24 sm:py-32 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500/10 border border-teal-500/20 text-teal-400 text-sm mb-6">
              <Sparkles className="w-4 h-4" />
              Warum marKIpilot?
            </motion.div>
            <motion.h2 variants={fadeInUp} className="font-display text-4xl sm:text-5xl font-bold mb-6">
              <span className="text-white">Die </span>
              <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">KI</span>
              <span className="text-white"> steckt in unserem Namen</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-slate-400 text-lg max-w-2xl mx-auto">
              mar<span className="text-teal-400 font-semibold">KI</span>pilot nutzt modernste künstliche Intelligenz, um Ihre App-Marketing-Performance auf ein neues Level zu heben.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-6">
            {[
              { 
                icon: Brain, 
                title: 'KI-Optimierte Creatives', 
                desc: 'Unsere AI analysiert tausende Ads und generiert datenbasierte Creative-Konzepte für maximale CTR und Hook-Rates.',
                gradient: 'from-purple-500 to-pink-500',
                stat: '↑ 340% CTR'
              },
              { 
                icon: Target, 
                title: 'Predictive Targeting', 
                desc: 'Machine Learning identifiziert Ihre profitabelsten Nutzersegmente bevor Sie Budget verschwenden.',
                gradient: 'from-teal-500 to-cyan-500',
                stat: '↓ 45% CPI'
              },
              { 
                icon: Zap, 
                title: 'Real-Time Bidding AI', 
                desc: 'KI-gesteuerte Gebotsstrategien optimieren Ihren CPI in Echtzeit über alle Kanäle hinweg.',
                gradient: 'from-orange-500 to-amber-500',
                stat: '24/7 aktiv'
              },
            ].map((item, i) => (
              <motion.div 
                key={i} 
                variants={fadeInScale} 
                className="group relative rounded-2xl bg-slate-900/50 border border-white/5 p-8 hover:border-white/10 transition-all duration-300 overflow-hidden"
              >
                {/* Gradient hover effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.gradient} flex items-center justify-center mb-6 shadow-lg`}>
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <div className="inline-block px-3 py-1 rounded-full bg-white/5 text-xs font-medium text-teal-400 mb-4">
                  {item.stat}
                </div>
                <h3 className="font-display text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-slate-400 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Industries Section - NEW DESIGN */}
      <section id="industries" className="py-24 sm:py-32 relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-sm mb-6">
              <Globe className="w-4 h-4" />
              Branchenspezialisierung
            </motion.div>
            <motion.h2 variants={fadeInUp} className="font-display text-4xl sm:text-5xl font-bold text-white mb-6">
              Für jede Branche die richtige<br />
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">KI-Strategie</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-slate-400 text-lg max-w-2xl mx-auto">
              Von Fintech bis Gaming – unsere KI kennt die Erfolgsformeln jeder App-Kategorie.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((ind, i) => (
              <motion.div 
                key={i} 
                variants={fadeInScale}
                className={`group relative rounded-2xl overflow-hidden border ${ind.borderColor} ${ind.bgColor} hover:scale-[1.02] transition-all duration-300`}
              >
                {/* Image */}
                <div className="relative h-40 overflow-hidden">
                  <img 
                    src={ind.image} 
                    alt={ind.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent" />
                  
                  {/* Stats Badge */}
                  <div className={`absolute top-4 right-4 px-3 py-1.5 rounded-full bg-gradient-to-r ${ind.color} text-white text-xs font-bold shadow-lg`}>
                    {ind.stats}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${ind.color} flex items-center justify-center`}>
                      <ind.icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-display text-lg font-bold text-white">{ind.title}</h3>
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed">{ind.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 sm:py-32 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm mb-6">
              <Rocket className="w-4 h-4" />
              Full-Service
            </motion.div>
            <motion.h2 variants={fadeInUp} className="font-display text-4xl sm:text-5xl font-bold text-white mb-6">
              Unsere Kompetenz-Säulen
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-8">
            {[
              { 
                icon: Cpu, 
                title: 'Tech & Attribution', 
                desc: 'Full-Stack MMP Integration, iOS 14+ SKAN Attribution, Deep-Link Architektur für maximale Conversion.',
                features: ['AppsFlyer / Adjust', 'SKAN 4.0 Ready', 'Deep-Link Setup'],
                image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&h=200&fit=crop'
              },
              { 
                icon: Palette, 
                title: 'Creative Factory', 
                desc: 'KI-generierte UGC & Viral-Marketing, Playable HTML5-Ads, psychologische ASO-Optimierung.',
                features: ['KI-Creatives', 'Playable Ads', 'A/B Testing'],
                image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=400&h=200&fit=crop'
              },
              { 
                icon: Megaphone, 
                title: 'Performance Media', 
                desc: 'Plattform-Dominanz auf Apple Search Ads, Google, Meta & TikTok. KI-Optimierung auf In-App-Events.',
                features: ['Apple Search Ads', 'Meta & Google', 'TikTok Ads'],
                image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=200&fit=crop'
              },
            ].map((s, i) => (
              <motion.div 
                key={i} 
                variants={fadeInScale} 
                className="group rounded-2xl overflow-hidden bg-slate-900/50 border border-white/5 hover:border-teal-500/30 transition-all duration-300"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={s.image} 
                    alt={s.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center shadow-lg">
                      <s.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="font-display text-xl font-bold text-white mb-3">{s.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-4">{s.desc}</p>
                  <div className="space-y-2">
                    {s.features.map((f, j) => (
                      <div key={j} className="flex items-center gap-2 text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                        <span className="text-slate-300">{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* PPI Model */}
      <section className="py-24 sm:py-32 relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-sm mb-6">
                <Shield className="w-4 h-4" />
                Null Risiko
              </motion.div>
              <motion.h2 variants={fadeInUp} className="font-display text-4xl sm:text-5xl font-bold text-white mb-6">
                Pay-per-Install:<br />
                <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">Wir investieren zuerst</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-slate-400 text-lg mb-8">
                Während andere Agenturen Retainer verlangen, setzen wir unser KI-Know-how ein und investieren in Ihr Wachstum. Sie zahlen nur für verifizierte Downloads.
              </motion.p>
              <motion.div variants={fadeInUp} className="space-y-4">
                {[
                  { label: 'Risiko für Sie', value: 'Null', icon: Shield },
                  { label: 'KI-Optimierung', value: 'Inklusive', icon: Brain },
                  { label: 'Kosten-Transparenz', value: '100% planbar', icon: BarChart3 },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/5">
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">{item.label}</p>
                      <p className="text-lg font-bold text-white">{item.value}</p>
                    </div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=600&h=400&fit=crop" 
                  alt="Team Collaboration"
                  className="w-full h-auto"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section id="process" className="py-24 sm:py-32 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm mb-6">
              <Rocket className="w-4 h-4" />
              Unser Prozess
            </motion.div>
            <motion.h2 variants={fadeInUp} className="font-display text-4xl sm:text-5xl font-bold text-white">
              In 5 Schritten zum Wachstum
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-teal-500/0 via-teal-500/50 to-teal-500/0 -translate-y-1/2" />
            
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-6">
              {[
                { step: '01', title: 'KI-Audit', desc: 'Tracking & Potenzial', icon: Target },
                { step: '02', title: 'Creative Sprint', desc: 'KI-Varianten', icon: Palette },
                { step: '03', title: 'Soft Launch', desc: 'Test & Basis-CPI', icon: Rocket },
                { step: '04', title: 'Scaling', desc: 'KI-Skalierung', icon: TrendingUp },
                { step: '05', title: 'Retention', desc: 'Optimierung', icon: Zap },
              ].map((s, i) => (
                <motion.div 
                  key={i} 
                  variants={fadeInUp}
                  className="relative bg-slate-900/50 rounded-2xl border border-white/5 p-6 text-center hover:border-teal-500/30 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-teal-500/25">
                    <s.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="font-display text-xs font-bold text-teal-400 mb-2">{s.step}</div>
                  <h3 className="font-display text-lg font-bold text-white mb-1">{s.title}</h3>
                  <p className="text-sm text-slate-500">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-24 sm:py-32 relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500/10 border border-teal-500/20 text-teal-400 text-sm mb-6">
                <Send className="w-4 h-4" />
                Let's Talk
              </motion.div>
              <motion.h2 variants={fadeInUp} className="font-display text-4xl sm:text-5xl font-bold text-white mb-6">
                Starten Sie Ihr<br />
                <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">KI-Wachstumsaudit</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-slate-400 text-lg mb-8">
                Erhalten Sie eine kostenlose KI-Analyse Ihres App-Wachstumspotenzials. Wir prüfen Ihr Tracking, Ihre Creatives und identifizieren Skalierungschancen.
              </motion.p>
              <motion.div variants={fadeInUp} className="space-y-4">
                {['Kostenlose KI-Erstberatung', 'Tracking-Audit inklusive', 'Unverbindliches PPI-Angebot'].map((t, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-teal-500/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-teal-400" />
                    </div>
                    <span className="text-slate-300">{t}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInScale}>
              <form onSubmit={handleSubmit} className="rounded-2xl bg-slate-900/50 border border-white/5 p-8 space-y-5 backdrop-blur-xl">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-300 mb-2 block">Vorname *</label>
                    <Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} placeholder="Max" className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-teal-500" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-300 mb-2 block">Nachname</label>
                    <Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} placeholder="Mustermann" className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-teal-500" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">E-Mail *</label>
                  <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="max@beispiel.de" className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-teal-500" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-300 mb-2 block">App / Produkt</label>
                    <Input value={form.app_name} onChange={e => setForm({...form, app_name: e.target.value})} placeholder="Ihre App" className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-teal-500" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-300 mb-2 block">Budget (optional)</label>
                    <Input value={form.budget} onChange={e => setForm({...form, budget: e.target.value})} placeholder="z.B. 10.000€/Monat" className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-teal-500" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">Nachricht</label>
                  <Textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="Erzählen Sie uns von Ihrer App..." rows={4} className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-teal-500 resize-none" />
                </div>
                <Button type="submit" disabled={sending} className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white border-0 h-12 text-base shadow-lg shadow-teal-500/25">
                  {sending ? 'Wird gesendet...' : 'KI-Audit starten'} {!sending && <ArrowRight className="w-5 h-5 ml-2" />}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      <FaqSection faqs={LANDING_FAQS} title="Häufige Fragen zu marKIpilot" dark />

      {/* Footer - with safe area padding at bottom */}
      <footer className="border-t border-white/5 bg-slate-950" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {/* Main Footer */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            
            {/* Company Info */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shadow-lg shadow-teal-500/25">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <span className="font-display font-bold text-xl text-white">
                  mar<span className="text-teal-400">KI</span>pilot
                </span>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Ihr KI-Pilot für App-Wachstum. Pay-per-Install Marketing mit modernster künstlicher Intelligenz.
              </p>
              <p className="text-slate-500 text-xs">
                Ein Service der MYSYMP AG
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-display font-bold text-white mb-6">Navigation</h4>
              <ul className="space-y-3">
                <li><a href="#ki" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">KI-Power</a></li>
                <li><a href="#industries" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">Branchen</a></li>
                <li><a href="#services" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">Leistungen</a></li>
                <li><a href="#process" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">Prozess</a></li>
                <li><a href="#contact" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">Kontakt</a></li>
                <li><Link to="/about" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">Über uns</Link></li>
                <li><Link to="/case-studies" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">Case Studies</Link></li>
                <li><Link to="/roi-rechner" className="text-slate-400 hover:text-teal-400 transition-colors text-sm" data-testid="footer-roi-link">ROI-Rechner</Link></li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-display font-bold text-white mb-6">Kompetenzen</h4>
              <ul className="space-y-3">
                <li><span className="text-slate-400 text-sm">Tech & Attribution</span></li>
                <li><span className="text-slate-400 text-sm">Creative Factory</span></li>
                <li><span className="text-slate-400 text-sm">Performance Media</span></li>
                <li><span className="text-slate-400 text-sm">ASO Optimierung</span></li>
                <li><span className="text-slate-400 text-sm">Retention & CRM</span></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-display font-bold text-white mb-6">Kontakt</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">MYSYMP AG</p>
                    <p className="text-slate-400 text-sm">Oberdorfstrasse 18a</p>
                    <p className="text-slate-400 text-sm">6207 Nottwil, Schweiz</p>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <a href="tel:+41794710625" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">
                    +41 79 471 06 25
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <a href="mailto:info@markipilot.com" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">
                    info@markipilot.com
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/5">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              {/* Copyright & Legal */}
              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-6">
                <span className="text-slate-500 text-xs">
                  © {new Date().getFullYear()} marKIpilot – Ein Service der MYSYMP AG
                </span>
                <div className="flex items-center gap-4">
                  <Link to="/impressum" className="text-slate-500 hover:text-slate-300 transition-colors text-xs">Impressum</Link>
                  <Link to="/datenschutz" className="text-slate-500 hover:text-slate-300 transition-colors text-xs">Datenschutz</Link>
                  <Link to="/agb" className="text-slate-500 hover:text-slate-300 transition-colors text-xs">AGB</Link>
                  <Link to="/about" className="text-slate-500 hover:text-slate-300 transition-colors text-xs">Über uns</Link>
                  <Link to="/case-studies" className="text-slate-500 hover:text-slate-300 transition-colors text-xs">Erfolge</Link>
                </div>
              </div>

              {/* Company Registration */}
              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-xs text-slate-600">
                <span>CHE-192.170.455</span>
                <span className="hidden sm:inline">|</span>
                <span>MWST: CHE-192.170.455</span>
              </div>

              {/* Social & Portal Links */}
              <div className="flex items-center gap-4">
                <a href="https://www.linkedin.com/company/mysymp" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors">
                  <svg className="w-4 h-4 text-slate-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <Link to="/login" className="text-xs text-slate-500 hover:text-teal-400 transition-colors">
                  Portal Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
